import React, { useEffect } from 'react';
import { useTerminal } from '../../hooks/useTerminal';
import CursorBlink from './CursorBlink';

const Terminal: React.FC = () => {
  const {
    state,
    suggestions,
    isProcessing,
    terminalRef,
    inputRef,
    handleInputChange,
    handleKeyDown,
    scrollToBottom,
    executeCommand
  } = useTerminal();

  useEffect(() => {
    console.log('Terminal component mounted');
    scrollToBottom();
  }, [scrollToBottom]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      console.log('Input focused');
    }
  }, []);

  // Debug logging
  useEffect(() => {
    console.log('Terminal state:', state);
    console.log('Terminal history length:', state.history.length);
    console.log('Current theme in Terminal component:', state.theme);
  }, [state]);

  // Force re-render when theme changes
  useEffect(() => {
    console.log('Theme changed in Terminal component to:', state.theme);
  }, [state.theme]);

  const getThemeColors = () => {
    console.log('Getting theme colors for theme:', state.theme);
    const colorClass = (() => {
      switch (state.theme) {
        case 'amber':
          return 'text-amber-500';
        case 'blue':
          return 'text-blue-400';
        case 'red':
          return 'text-red-400';
        case 'yellow':
          return 'text-yellow-300';
        case 'purple':
          return 'text-purple-400';
        default:
          return 'text-green-500';
      }
    })();
    console.log('Returning color class:', colorClass);
    return colorClass;
  };

  const getThemeBg = () => {
    switch (state.theme) {
      case 'amber':
        return 'bg-amber-500/20';
      case 'blue':
        return 'bg-blue-400/20';
      case 'red':
        return 'bg-red-400/20';
      case 'yellow':
        return 'bg-yellow-300/20';
      case 'purple':
        return 'bg-purple-400/20';
      default:
        return 'bg-green-500/20';
    }
  };

  const getThemeBorder = () => {
    switch (state.theme) {
      case 'amber':
        return 'border-amber-500/30';
      case 'blue':
        return 'border-blue-400/30';
      case 'red':
        return 'border-red-400/30';
      case 'yellow':
        return 'border-yellow-300/30';
      case 'purple':
        return 'border-purple-400/30';
      default:
        return 'border-green-500/30';
    }
  };

  // Handle quick command button clicks
  const handleQuickCommand = async (command: string) => {
    console.log('Quick command clicked:', command);
    if (executeCommand) {
      try {
        await executeCommand(command);
      } catch (error) {
        console.error('Error executing quick command:', error);
      }
    }
  };

  return (
    <div className={`terminal-container h-full w-full bg-black font-mono overflow-hidden relative ${getThemeColors()} text-sm sm:text-base`}>
      {/* Terminal Header */}
      <div className="bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
        </div>
        <div className="text-sm text-gray-400">
          Mohammad Tabish Portfolio - Terminal
        </div>
      </div>

      {/* Terminal Content */}
      <div 
        ref={terminalRef}
        className="flex-1 overflow-y-auto p-2 sm:p-4 space-y-2"
        style={{ height: 'calc(100% - 60px)' }}
      >
        {/* Welcome Message */}
        {state.history.length === 0 && (
          <div className="terminal-text mb-4">
            <div className="text-lg font-bold mb-2">🚀 Welcome to Mohammad Tabish's Portfolio Terminal!</div>
            <div className="text-sm text-gray-400 mb-4">
              Type 'help' to see available commands or start exploring with commands like 'about', 'skills', 'projects', etc.
            </div>
            <div className="text-xs text-gray-500">
              💡 Tip: Use Tab for command completion and arrow keys for command history
            </div>
            <div className="text-xs text-yellow-500 mt-2">
              🔧 All commands are now fully functional! Try typing 'help' or use the quick command buttons below.
            </div>
            <div className="text-xs text-blue-400 mt-2">
              📝 Essential: help, about, skills, projects, experience, education, contact
            </div>
            <div className="text-xs text-green-400 mt-1">
              🔗 Advanced: schedule, message, timeline, achievements, certifications, research
            </div>
          </div>
        )}

        {/* Command History */}
        {state.history.map((entry, index) => (
          <div key={index} className="terminal-text whitespace-pre-wrap">
            {entry.content}
          </div>
        ))}

        {/* Current Input Line - Always visible at bottom */}
        <div className={`flex items-center mt-2 sm:mt-4 sticky bottom-0 bg-black py-2 ${getThemeBorder()}`} style={{ borderTop: `1px solid ${state.theme === 'green' ? 'rgba(0, 255, 0, 0.3)' : state.theme === 'amber' ? 'rgba(251, 191, 36, 0.3)' : state.theme === 'blue' ? 'rgba(96, 165, 250, 0.3)' : state.theme === 'red' ? 'rgba(248, 113, 113, 0.3)' : state.theme === 'yellow' ? 'rgba(250, 204, 21, 0.3)' : 'rgba(168, 85, 247, 0.3)'}` }}>
          <span className={`terminal-text mr-1 sm:mr-2 font-bold ${getThemeColors()} text-xs sm:text-base`}>guest@tabish-portfolio:~$</span>
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={state.currentCommand}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className={`terminal-input-field w-full bg-transparent outline-none placeholder-gray-600 text-base sm:text-lg ${getThemeColors()}`}
              placeholder="Type a command..."
              disabled={isProcessing}
              autoFocus
              data-testid="terminal-input"
              tabIndex={0}
              autoComplete="off"
              spellCheck={false}
            />
            <CursorBlink isActive={!isProcessing} />
          </div>
        </div>

        {/* Command Suggestions */}
        {suggestions.length > 0 && (
          <div className="mt-2">
            <div className="text-gray-500 text-sm mb-1">Suggestions:</div>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleInputChange(suggestion)}
                  className={`terminal-button px-2 py-1 rounded text-xs ${getThemeBg()} ${getThemeColors()} hover:opacity-80 transition-opacity`}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Processing Indicator */}
        {isProcessing && (
          <div className={`flex items-center space-x-2 terminal-text ${getThemeColors()}`}>
            <div className={`animate-spin rounded-full h-4 w-4 border-b-2 ${getThemeColors()}`}></div>
            <span>Processing...</span>
          </div>
        )}

        {/* Quick Command Buttons */}
        <div className={`mt-2 sm:mt-4 pt-2 sm:pt-4 ${getThemeBorder()}`}>
          <div className="text-xs text-gray-500 mb-2">Quick Commands:</div>
          <div className="flex flex-wrap gap-1 sm:gap-2">
            {['test', 'help', 'about', 'skills', 'projects', 'experience', 'education', 'certifications', 'research', 'contact'].map((cmd) => (
              <button
                key={cmd}
                onClick={() => handleQuickCommand(cmd)}
                className={`terminal-button px-2 sm:px-3 py-1 border rounded text-xs sm:text-sm hover:opacity-80 transition-opacity ${getThemeBg()} ${getThemeColors()} ${getThemeBorder()} min-h-[44px] touch-manipulation`}
              >
                {cmd}
              </button>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-1 sm:gap-2">
            {['schedule', 'message', 'timeline', 'achievements'].map((cmd) => (
              <button
                key={cmd}
                onClick={() => handleQuickCommand(cmd)}
                className={`terminal-button px-2 sm:px-3 py-1 border rounded text-xs sm:text-sm hover:opacity-80 transition-opacity ${getThemeBg()} ${getThemeColors()} ${getThemeBorder()} min-h-[44px] touch-manipulation`}
              >
                {cmd}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terminal;
